import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Flex } from '@/components/ui';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import {
  useDeleteWhitelistedStudyFile,
  useWhitelistedStudyFiles,
} from '@/services/whitelistedStudyFilesHooks';
import { FileImporterFile } from '../types';

import { IconAlertTriangleFilled } from '@tabler/icons-react';

import FileImporterFilesTable from './FileImporterFilesTable';
import { FileImporterFilesManagerProps } from './types';

interface ExtendedFileImporterFilesManagerProps
  extends FileImporterFilesManagerProps {
  canUpload?: boolean;
  customEmptyContent?: React.ReactNode;
}

const FileImporterFilesManager: React.FC<
  ExtendedFileImporterFilesManagerProps
> = ({ className, canUpload = false, customEmptyContent }) => {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // API hooks - memoize filters to prevent unnecessary requests
  const filesFilters = useMemo(
    () => ({
      page: pagination.pageIndex + 1, // API uses 1-based pagination
      per_page: pagination.pageSize,
      order: 'created_at',
      direction: 'desc' as const,
    }),
    [pagination.pageIndex, pagination.pageSize]
  );

  const { data, isLoading, error, refetch } = useWhitelistedStudyFiles(filesFilters);
  // Transform WhitelistedStudyFile to FileImporterFile format
  const files: FileImporterFile[] = useMemo(
    () =>
      (data?.files || []).map(file => ({
        ...file,
        file_type: 'whitelisted_study' as const,
      })),
    [data?.files]
  );
  const paginationInfo = data?.pagination;
  const deleteWhitelistedFile = useDeleteWhitelistedStudyFile();

  // Permission checks
  const canManageFiles = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);

  const handleDeleteFile = useCallback(
    async (file: FileImporterFile) => {
      setDeleteError(null);

      try {
        await deleteWhitelistedFile.mutateAsync(file.id);
      } catch (error) {
        setDeleteError(
          error instanceof Error ? error.message : 'Delete failed'
        );
      }
    },
    [deleteWhitelistedFile]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  return (
    <Flex
      className={className}
      direction="column"
      gap="4"
      style={{
        minHeight: '100%',
        width: '100%',
      }}
    >
      {/* Delete errors */}
      {deleteError && (
        <Alert color="error" title="Delete Error">
          {deleteError}
        </Alert>
      )}

      {/* General API errors */}
      {error && (
        <Alert
          color="error"
          title="Failed to load files. Please try again."
          variant="soft"
          icon={<IconAlertTriangleFilled />}
        ></Alert>
      )}

      {/* Files table */}
      <FileImporterFilesTable
        files={files}
        loading={isLoading}
        onDeleteFile={canManageFiles ? handleDeleteFile : undefined}
        onRefresh={handleRefresh}
        pagination={paginationInfo}
        paginationState={pagination}
        onPaginationChange={handlePaginationChange}
        customEmptyContent={customEmptyContent}
        canUpload={canUpload}
      />
    </Flex>
  );
};

export default FileImporterFilesManager;
